/**
 * User tags → Automatic tags: the rules behind the tags that look after
 * themselves (user_tags.auto_rules; owner, 2026-09-30).
 *
 * Each tag is one rule, "<measure> <comparison> <value>", over numbers every
 * account already has (searches — the Searches count on User 360 — AI chats,
 * saved cards, sign-ins, profile %, days since the last sign-in, …). Edit a
 * rule, switch it off, or add your own; the count beside each rule is how
 * many people carry it now, and the one under an edited rule is how many
 * would. Built-ins cannot be deleted, only edited, reset or switched off.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Btn, Loading, Msg, Section, panelGet, panelPost, useAdminGet } from '../kit';

interface Rule {
  key: string; label: string; color: string; description: string;
  metric: string; op: string; value: number; enabled: boolean; builtin?: boolean; n?: number;
}
interface Metric { key: string; label: string; description: string }

const EDIT_KEYS: (keyof Rule)[] = ['label', 'color', 'description', 'metric', 'op', 'value', 'enabled'];
const same = (a: Rule, b: Rule) => EDIT_KEYS.every(k => String(a[k]) === String(b[k]));
// Trimmed AFTER the cut, or a label cut at a word break ends the key in '-'
// (the server normalises a new key too).
const slug = (s: string) => s.toLowerCase().normalize('NFKD').replace(/[^a-z0-9]+/g, '-').replace(/^-+/, '')
  .slice(0, 34).replace(/-+$/, '');
const OP_LABELS: Record<string, string> = { '>=': 'at least', '>': 'more than', '=': 'exactly', '<=': 'at most', '<': 'less than' };

/** "would match N" for an edited rule, debounced. */
const Preview: React.FC<{ r: Rule }> = ({ r }) => {
  const [n, setN] = useState<number | null>(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    let dead = false;
    const t = setTimeout(async () => {
      const { data, error } = await panelGet('tags', 'auto_rule_preview', { metric: r.metric, op: r.op, value: r.value });
      if (dead) return;
      if (error) { setErr(error); setN(null); } else { setErr(''); setN(data?.n ?? null); }
    }, 350);
    return () => { dead = true; clearTimeout(t); };
  }, [r.metric, r.op, r.value]);
  if (err) return <span className="text-[11px] text-red-700">{err}</span>;
  return <span className="text-[11px] text-primary-700">{n == null ? 'counting…' : `would match ${n.toLocaleString()}`}</span>;
};

const AutoRules: React.FC<{ onOpen: (key: string) => void }> = ({ onOpen }) => {
  const q = useAdminGet('tags', 'auto_rules', {}, []);
  const [draft, setDraft] = useState<Rule[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const saved: Rule[] = q.data?.rules || [];
  const metrics: Metric[] = q.data?.metrics || [];
  const ops: string[] = q.data?.ops || ['>=', '>', '=', '<=', '<'];
  const defaults: Record<string, Rule> = q.data?.defaults || {};
  const lastLoaded = useRef<any>(null);

  // A fresh server state resets the draft (after a save, or on first load).
  useEffect(() => {
    if (q.data && q.data !== lastLoaded.current) {
      lastLoaded.current = q.data;
      setDraft((q.data.rules || []).map((r: Rule) => ({ ...r })));
    }
  }, [q.data]);

  const byKey = useMemo(() => Object.fromEntries(saved.map(r => [r.key, r])), [saved]);
  const rows = draft || [];
  const dirty = rows.length !== saved.length || rows.some(r => !byKey[r.key] || !same(r, byKey[r.key]));
  const metricOf = (k: string) => metrics.find(m => m.key === k);

  const patch = (key: string, p: Partial<Rule>) =>
    setDraft(d => (d || []).map(r => (r.key === key ? { ...r, ...p } : r)));

  const add = () => {
    const base = 'New tag';
    let n = 1;
    const keys = new Set(rows.map(r => r.key));
    while (keys.has(`auto-${slug(`${base} ${n}`)}`)) n += 1;
    setDraft(d => [...(d || []), {
      key: `auto-${slug(`${base} ${n}`)}`, label: `${base} ${n}`, color: '#475569', description: '',
      metric: 'searches', op: '>=', value: 1, enabled: true, builtin: false,
    }]);
  };

  const save = async () => {
    setBusy(true); setNote(null);
    // A new tag takes its key from its name (auto-<name>), unless that key is taken.
    const taken = new Set(rows.filter(r => byKey[r.key]).map(r => r.key));
    const keyed = rows.map(r => {
      if (byKey[r.key]) return r;
      const want = `auto-${slug(r.label) || 'tag'}`;
      let k = want;
      for (let i = 2; taken.has(k); i += 1) k = `${want.slice(0, 36)}-${i}`;
      taken.add(k);
      return { ...r, key: k };
    });
    const { data, error } = await panelPost('tags', 'auto_rules_save', { rules: keyed.map(r => ({
      key: r.key, label: r.label.trim(), color: r.color, description: r.description.trim(),
      metric: r.metric, op: r.op, value: Number(r.value), enabled: r.enabled })) });
    setBusy(false);
    if (error) { setNote({ kind: 'error', text: error }); return; }
    setNote({ kind: 'ok', text: 'Saved. The tags follow the new rules everywhere within two minutes.' });
    lastLoaded.current = null;
    q.reload();
    void data;
  };

  // Reset is a draft change like any other (Save applies it), so other
  // unsaved edits are never thrown away by a reload.
  const resetOne = (key: string) => {
    const d = defaults[key];
    if (d) patch(key, { label: d.label, color: d.color, description: d.description, metric: d.metric,
      op: d.op, value: d.value, enabled: true });
  };

  if (q.loading && !q.data) return <Loading />;
  return (
    <div className="flex flex-col gap-4">
      {q.error && <Msg kind="error">{q.error}</Msg>}
      {note && <Msg kind={note.kind}>{note.text}</Msg>}
      <Section title="Automatic tags — the rules"
        right={<div className="flex gap-2">
          <Btn small onClick={add} disabled={busy || rows.length >= 30}>Add a tag</Btn>
          {dirty && <Btn small onClick={() => setDraft(saved.map(r => ({ ...r })))} disabled={busy}>Undo changes</Btn>}
          <Btn small kind="primary" onClick={save} disabled={busy || !dirty}>{busy ? 'Saving…' : 'Save rules'}</Btn>
        </div>}>
        <p className="text-xs text-grayscale-1100 -mt-1">
          These tags are worked out from each account's numbers — nobody assigns them. Each is one rule. The
          count is how many people carry the tag now ({(q.data?.people ?? 0).toLocaleString()} accounts).
          <b> Searches</b> is the Searches count on User 360; blocked search attempts and AI chats are separate
          measures. Use them in any tag filter, e.g. Lifecycle <i>Explorer</i> + without <i>Search Started</i>.
        </p>
        <p className="text-xs text-grayscale-1100">
          <b>Days since …</b> measures (last sign-in, activity, search, AI chat, e-mail, account age) count whole
          days and roll forward on their own. People who never did the thing have no value and never match, so
          <i> Login within 7 days</i> leaves out those who never signed in (they carry <i>Never logged in</i>).
          A sign-in is a new sign-in, not a visit: a session that renews itself can show up to 30 days, so use
          <i> Days since last activity</i> to catch everyone who came back. For a band, filter with one tag and
          without another, e.g. <i>Login within 60 days</i> without <i>Login within 30 days</i>.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-grayscale-1100 border-b border-grayscale-300">
                <th className="py-2 pr-3">Tag</th>
                <th className="py-2 pr-3">Rule</th>
                <th className="py-2 pr-3 text-right">People</th>
                <th className="py-2 pr-3">On</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const orig = byKey[r.key];
                const changed = !orig || !same(r, orig);
                const ruleChanged = !orig || orig.metric !== r.metric || orig.op !== r.op || String(orig.value) !== String(r.value);
                const def = defaults[r.key];
                const offDefault = !!def && !same({ ...r }, { ...def, enabled: true } as Rule);
                return (
                  <tr key={r.key} className={`border-b border-grayscale-200 align-top ${changed ? 'bg-primary-100/40' : ''} ${r.enabled ? '' : 'opacity-60'}`}>
                    <td className="py-2 pr-3 min-w-[15rem]">
                      <div className="flex items-center gap-2">
                        <input type="color" value={r.color} onChange={e => patch(r.key, { color: e.target.value })}
                          className="w-6 h-6 rounded border border-grayscale-300 p-0 shrink-0" title="Colour" />
                        <input value={r.label} maxLength={60} onChange={e => patch(r.key, { label: e.target.value })}
                          className="h-8 px-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm font-medium w-full min-w-0" />
                      </div>
                      <input value={r.description} maxLength={300} placeholder="What it means (shown on hover)"
                        onChange={e => patch(r.key, { description: e.target.value })}
                        className="mt-1 h-7 px-2 rounded-md border-[0.5px] border-grayscale-300 bg-white text-xs w-full" />
                      <div className="text-[10px] text-grayscale-1100 mt-0.5">{r.key}{r.builtin ? ' · built-in' : ''}</div>
                    </td>
                    <td className="py-2 pr-3 min-w-[19rem]">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <select value={r.metric} onChange={e => patch(r.key, { metric: e.target.value })}
                          className="h-8 px-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm max-w-[14rem]"
                          title={metricOf(r.metric)?.description}>
                          {metrics.map(m => <option key={m.key} value={m.key}>{m.label}</option>)}
                        </select>
                        <select value={r.op} onChange={e => patch(r.key, { op: e.target.value })}
                          className="h-8 px-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm">
                          {ops.map(o => <option key={o} value={o}>{OP_LABELS[o] || o}</option>)}
                        </select>
                        <input type="number" min={0} step={1} value={r.value}
                          onChange={e => patch(r.key, { value: e.target.value === '' ? 0 : Math.max(0, Math.floor(Number(e.target.value))) })}
                          className="h-8 w-20 px-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm" />
                      </div>
                      <div className="text-[11px] text-grayscale-1100 mt-1">{metricOf(r.metric)?.description}</div>
                      {ruleChanged && <Preview r={r} />}
                    </td>
                    <td className="py-2 pr-3 text-right tabular-nums">
                      <button type="button" onClick={() => onOpen(r.key)} disabled={!orig || !orig.enabled || dirty}
                        className="font-bold hover:underline disabled:no-underline disabled:cursor-default"
                        title={dirty ? 'Save or undo your changes first' : orig?.enabled ? 'Open these accounts'
                          : 'Save and switch on to open the accounts'}>
                        {(orig?.n ?? 0).toLocaleString()}
                      </button>
                    </td>
                    <td className="py-2 pr-3">
                      <input type="checkbox" checked={r.enabled} onChange={e => patch(r.key, { enabled: e.target.checked })}
                        className="w-4 h-4 accent-[#9A7A4C]" aria-label={`${r.label} on`} />
                    </td>
                    <td className="py-2 whitespace-nowrap text-right">
                      {r.builtin && offDefault && (
                        <button type="button" onClick={() => resetOne(r.key)} disabled={busy}
                          className="text-xs underline text-grayscale-1200" title={`Default: ${def.label} — ${def.metric} ${def.op} ${def.value}`}>
                          Reset
                        </button>
                      )}
                      {!r.builtin && (
                        <button type="button" disabled={busy}
                          onClick={() => setDraft(d => (d || []).filter(x => x.key !== r.key))}
                          className="text-xs underline text-red-700">Delete</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {dirty && <div className="text-[11px] text-amber-800">Unsaved changes — Save rules to apply them.</div>}
      </Section>
    </div>
  );
};

export default AutoRules;
